/**
 * Slim freshness bar for the Inventory Audit page — per the approved remodel
 * mockup (`.collapsed-bar`), this is ALL the upload machinery that remains on
 * the audit page, and since 2026-09-14 it also HOSTS the run selector: "Run:
 * [Latest — Aug 13, 2:27 PM ▾] · files from Aug 11" plus a "Start new run"
 * button that opens the dedicated New Audit Run page. The selector used to
 * sit in the page header next to Download, which repeated the same run date
 * twice on one screen (Ian: "its redundant"). The "files from" clause still
 * describes the currently STAGED inputs, regardless of which older run the
 * selector is viewing.
 */
import React from "react"

import { AuditFileStatus, AuditRunSummary } from "../../api/audit"
import { useAuth } from "../../context/AuthContext"
import { THEME } from "../../config/theme"
import { parseUtcTimestamp } from "../../utils/parseUtcTimestamp"
import RunSelector from "./RunSelector"

interface Props {
  /** The group's runs, newest first — fed straight into the run selector. */
  runs: AuditRunSummary[]
  /** Staged-file status for the "files from" clause; null hides that clause. */
  status: AuditFileStatus | null
  /** True while the runs list is still loading — avoids a false "No runs yet". */
  loading?: boolean
  runsError?: string | null
  /** Opens the New Audit Run page — the owning page supplies the navigation. */
  onStartNewRun: () => void
}

/** "today" / "yesterday" / "Aug 11" in the viewer's local calendar. */
function dayWord(date: Date): string {
  const now = new Date()
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  if (diffDays === 0) return "today"
  if (diffDays === 1) return "yesterday"
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

/**
 * The staged files' weakest link: the OLDEST modified time among present
 * slots — a run is only as fresh as its stalest input. Null when nothing
 * is staged.
 */
function oldestStagedFileDate(status: AuditFileStatus): Date | null {
  const stamps = [status.tekion, status.homenet, ...status.rapid_recon]
    .filter((slot) => slot.present && slot.modified)
    .map((slot) => parseUtcTimestamp(slot.modified as string))
  if (stamps.length === 0) return null
  return stamps.reduce((oldest, d) => (d < oldest ? d : oldest))
}

export default function AuditFreshnessBar({
  runs, status, loading = false, runsError = null, onStartNewRun,
}: Props) {
  const { hasPermission } = useAuth()

  const filesDate = status ? oldestStagedFileDate(status) : null

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: THEME.spacing.sm,
        border: `1px solid ${THEME.colors.neutral.light.border}`,
        borderRadius: THEME.borderRadius.md,
        padding: "10px 14px",
        fontSize: 13,
        color: THEME.colors.neutral.light.textSecondary,
        background: THEME.problemDisplay.legendBackground,
        marginBottom: THEME.spacing.md,
      }}
    >
      <RunSelector runs={runs} loading={loading} error={runsError} />
      {filesDate && <span>· files from {dayWord(filesDate)}</span>}
      {hasPermission("run_audit") && (
        <button
          type="button"
          onClick={onStartNewRun}
          style={{
            marginLeft: "auto",
            fontSize: 12.5,
            fontWeight: 500,
            fontFamily: THEME.fonts.primary,
            padding: "6px 12px",
            border: `1px solid ${THEME.colors.navy}`,
            borderRadius: THEME.borderRadius.sm,
            background: THEME.colors.navy,
            color: THEME.colors.white,
            cursor: "pointer",
          }}
        >
          Start new run
        </button>
      )}
    </div>
  )
}
